'use client';

/**
 * Lightweight context that lets a host surface (e.g. an `AgentNode` on
 * a workspace canvas) intercept actions that would otherwise navigate
 * the whole page away from itself.
 *
 * Today the only handler is `onOpenContent` — wired by
 * `<DocumentChip>` so chip clicks spawn a sibling content widget next
 * to the agent instead of `router.push`'ing to `/content/<id>` and
 * tearing the user out of the workspace.
 *
 * The default value (no provider) is `{}` — consumers fall back to
 * their normal navigation behaviour, which is exactly what we want
 * for standalone chat surfaces (`/chats/<id>`, `/[username]`, …).
 */

import { createContext, useContext } from 'react';

export interface ChatHostHandlers {
  /**
   * Called when the user clicks a content chip / preview. Return `true`
   * (or a `Promise<true>`) to claim the click and suppress the default
   * navigation; any other return value falls through to
   * `router.push('/content/<id>')`. Hosts that perform an async spawn
   * should still return `true` immediately — the spawn doesn't block
   * the click handler.
   */
  onOpenContent?: (
    contentId: string,
    meta?: { filename?: string; mimeType?: string },
  ) => boolean | void | Promise<boolean | void>;

  /**
   * Called when the user clicks an `@username` mention link rendered by
   * `<Markdown>` (or any other host-aware profile link). Return `true`
   * to claim the click and suppress the default `router.push('/u/<name>')`
   * navigation. The `hint.accountType` (when known by the caller) lets a
   * host short-circuit a user/agent disambiguation fetch.
   *
   * Wired by `AgentNode` to spawn a sibling participant widget to the
   * right of the originating agent instead of tearing the user out of
   * the workspace.
   */
  onOpenProfile?: (
    username: string,
    hint?: { accountType?: 'user' | 'agent' },
  ) => boolean | void | Promise<boolean | void>;

  /**
   * Called when the user clicks a historical `delegate` tool-call row in
   * chat history. The host (typically `AgentNode`) re-creates the
   * delegation chain on the canvas. Return `true` to claim the click and
   * suppress fallback behaviour (which is the inline sub-chat preview).
   *
   * `chatId` is the parent chat the click happened in. The replay
   * implementation uses it as a last-resort recovery path when neither
   * `data.subChatId` nor `args` carry enough info to resolve the callee
   * — it fetches the parent chat's tool rows and reads `subChatId` off
   * `metadata.tool_data` on the row matching `callId`. Without this the
   * click silently no-ops on chips whose live overlay never received a
   * `tool_result` with `data` (mostly a streaming-race symptom on
   * sub-chat ids issued mid-turn).
   */
  onReplayDelegation?: (toolCall: {
    callId: string;
    args: unknown;
    result?: unknown;
    data?: { subChatId?: string };
    chatId?: string;
  }) => boolean | void | Promise<boolean | void>;

  /**
   * Called when the user clicks "Turn on app building" on the card the
   * chat strip renders for a refused app-shaped `text_editor create`
   * (`lib/llm/app-build-guard.ts`, decision D2, 2026-09-05). Return `true`
   * to claim the click. The workspace `AgentNode` wires this to flip the
   * hosting tile into its settings view; `ChatView` itself supplies a
   * standalone fallback that spawns the agent on the workspace, since
   * `useWorkspaceActions` throws off-canvas and the card must never reach
   * for it directly.
   */
  onOpenAgentSettings?: () => boolean | void;

  /**
   * Identity of the workspace AgentNode hosting this chat. ChatView is
   * workspace-agnostic; it forwards this to delegation/present routing so
   * the dispatch knows which canvas item owns the spawn provenance.
   */
  hostingAgentItemId?: string;

  /**
   * Root of the current delegation chain. For the topmost AgentNode this
   * equals `hostingAgentItemId`; for delegation children it is the root
   * agent the user originally talked to, inherited via `state.cache`.
   * Used by `useDelegationVisualization` to tag pucks so an entire chain
   * can be torn down on the next outer-level delegation start.
   */
  rootHostingAgentItemId?: string;

  /**
   * The id of the chat currently rendered by ChatView. Forwarded to
   * `onReplayDelegation` so the replay implementation has a stable
   * fallback path back to persisted `tool_data.subChatId` when the
   * live overlay didn't carry one. Undefined for draft chats.
   */
  chatId?: string;
}

const ChatHostContext = createContext<ChatHostHandlers>({});

export function ChatHostProvider({
  value,
  children,
}: {
  value: ChatHostHandlers;
  children: React.ReactNode;
}) {
  return (
    <ChatHostContext.Provider value={value}>{children}</ChatHostContext.Provider>
  );
}

export function useChatHost(): ChatHostHandlers {
  return useContext(ChatHostContext);
}
